/**
 * Конструктор ОДНОГО своего поля названия тех.операции (AK-564).
 *
 * Решение founder 3 (10.08): «конструктор доп. полей — в карточку записи».
 * Раньше поля жили в отдельной панели, вход в которую был колонкой-ссылкой в
 * списке («операция 2 · работа 1»): список платил колонкой за разовую
 * настройку, а карточка про эти поля не знала. Теперь список полей — секция
 * карточки, а эта форма открывается ПОВЕРХ неё и заводит ровно одно поле.
 *
 * Правка поля на месте по-прежнему не поддержана намеренно: у бэка есть только
 * добавление и удаление по ключу, а «правка» ключа = молчаливая потеря связи с
 * уже записанными `attrs`. Удалить + завести заново — честнее.
 */
import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/lib/toast';
import {
  useAddExtraField,
  type CompanyOpType,
  type ExtraFieldScope,
} from '@/lib/reference/companyOperationTypes';
import {
  EMPTY_DRAFT,
  FIELD_TYPES,
  draftToFieldDef,
  validateDraft,
  type ExtraFieldDraft,
} from './opTypeFieldForm';

export function OpTypeFieldDialog({
  item,
  open,
  onOpenChange,
}: {
  item: CompanyOpType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [scope, setScope] = useState<ExtraFieldScope>('operation');
  const [draft, setDraft] = useState<ExtraFieldDraft>(EMPTY_DRAFT);
  const [localError, setLocalError] = useState<string | null>(null);
  const addField = useAddExtraField();

  useEffect(() => {
    if (!open) return;
    setScope('operation');
    setDraft(EMPTY_DRAFT);
    setLocalError(null);
    addField.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!item) return null;

  const existing = scope === 'operation' ? item.extra_fields : item.extra_work_fields;

  function submit() {
    if (!item) return;
    const problem = validateDraft(
      draft,
      existing.map((f) => f.key),
    );
    setLocalError(problem);
    if (problem) return;
    addField.mutate(
      { id: item.id, scope, field: draftToFieldDef(draft) },
      {
        onSuccess: () => {
          toast.success('Поле добавлено');
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message || 'Не удалось добавить поле'),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Своё поле · {item.local_name}</DialogTitle>
          <DialogDescription>
            Что ещё записывать по этой работе сверх системных полей.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="fld-scope">Когда заполняется</Label>
            <Select
              value={scope}
              onValueChange={(v) => setScope(v as ExtraFieldScope)}
            >
              <SelectTrigger id="fld-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="operation">Один раз на операцию</SelectItem>
                <SelectItem value="work">На каждую смену исполнителя</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              «Партия семян» — один раз на операцию. «Глубина обработки» и
              «расход топлива» — на каждую смену: у механизаторов они свои.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="fld-label">Название поля</Label>
            <Input
              id="fld-label"
              placeholder="Например, Глубина обработки"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="fld-key">Ключ</Label>
            <Input
              id="fld-key"
              placeholder="depth_cm"
              value={draft.key}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Латиница, цифры и «_». По ключу поле уезжает в обмен с внешними
              системами — менять его потом нельзя.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="fld-type">Тип значения</Label>
            <Select
              value={draft.type}
              onValueChange={(v) => setDraft({ ...draft, type: v })}
            >
              <SelectTrigger id="fld-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {draft.type === 'enum' && (
            <div className="grid gap-2">
              <Label htmlFor="fld-options">Варианты</Label>
              <Input
                id="fld-options"
                placeholder="через запятую: сплошная, ленточная"
                value={draft.options}
                onChange={(e) => setDraft({ ...draft, options: e.target.value })}
              />
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="fld-unit">Единица измерения</Label>
            <Input
              id="fld-unit"
              placeholder="см, л/га — необязательно"
              value={draft.unit}
              onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="fld-required"
              checked={draft.required}
              onCheckedChange={(v) => setDraft({ ...draft, required: v === true })}
            />
            <Label htmlFor="fld-required">Заполнять обязательно</Label>
          </div>

          {(localError || addField.error) && (
            <Alert variant="destructive">
              <AlertDescription>
                {localError ?? addField.error?.message}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={addField.isPending}
          >
            Отмена
          </Button>
          <Button onClick={submit} disabled={addField.isPending}>
            {addField.isPending ? 'Добавляем…' : 'Добавить поле'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OpTypeFieldDialog;
